import React, { useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { api } from "@/lib/api";
import { runAIPrompt } from "@/lib/ai";
import { PageHeader, StatusBadge, EmptyState } from "@/components/common/Bits";
import { FileOutput, Sparkles, Download, Trash2, Printer } from "lucide-react";
import { toast } from "sonner";
import { useAIOutputs } from "@/hooks/useAIOutputs";

const REPORT_TYPES = [
  { key: "case_summary", title: "Master Υπόμνημα Υπόθεσης" },
  { key: "trial_preparation", title: "Σύνοψη Προετοιμασίας Δίκης" },
  { key: "witness_contradictions", title: "Ανάλυση Μαρτύρων" },
  { key: "prosecution_weaknesses", title: "Αδυναμίες Κατηγορίας" },
  { key: "timeline", title: "Αναφορά Χρονολογίου" },
  { key: "cross_examination", title: "Σχέδιο Αντεξέτασης" },
  { key: "hearing_prep", title: "Υπόμνημα Δικασίμου" },
  { key: "appeal_grounds", title: "Υπόμνημα Λόγων Έφεσης" },
  { key: "defense_theory", title: "Υπόμνημα Υπερασπιστικής Στρατηγικής" },
];

function mdToHtml(md) {
  if (!md) return "";
  let html = md
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^### (.*$)/gm, "<h3>$1</h3>")
    .replace(/^## (.*$)/gm, "<h2>$1</h2>")
    .replace(/^# (.*$)/gm, "<h1>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/^\- (.+)$/gm, "<li>$1</li>")
    .replace(/^\d+\. (.+)$/gm, "<li>$1</li>")
    .replace(/^&gt; (.+)$/gm, "<blockquote>$1</blockquote>");
  html = html.replace(/(<li>.*<\/li>\n?)+/g, m => `<ul>${m}</ul>`);
  html = html.split(/\n\n+/).map(p => /^<(h\d|ul|blockquote)/.test(p) ? p : `<p>${p}</p>`).join("\n");
  return html;
}

export default function Reports() {
  const { t, currentCase } = useApp();
  const { outputs, addOutput, removeOutput } = useAIOutputs(currentCase?.id);
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [model, setModel] = useState("claude");

  const generate = async (key) => {
    setGenerating(key);
    try {
      const result = await runAIPrompt({ prompt_key: key, case_id: currentCase.id, model });
      const out = {
        id: result.output_id, title: result.title, model: result.model,
        confidence: result.confidence, output: result.output,
        created_at: result.completed_at, prompt_key: key,
      };
      addOutput(out);
      setSelected(out);
      toast.success(`Παραδοτέο: ${result.title}`);
    } catch (e) { toast.error(e.message || "Σφάλμα AI"); }
    finally { setGenerating(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Διαγραφή;")) return;
    await api.delete(`/cases/${currentCase.id}/ai-outputs/${id}`);
    removeOutput(id);
    if (selected?.id === id) setSelected(null);
  };

  const printReport = () => {
    if (!selected) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const html = `<!doctype html><html lang="el"><head><meta charset="utf-8"><title>${selected.title}</title>
      <style>
        body{font-family:'Cormorant Garamond', Georgia, serif; max-width: 800px; margin: 40px auto; padding: 40px; color: #111; }
        .cover{ text-align:center; padding: 80px 0; border-bottom: 2px solid #B8860B; margin-bottom:60px; page-break-after:always; }
        .cover .brand{ font-family:'Manrope', sans-serif; letter-spacing: 8px; color:#B8860B; font-size:11px; text-transform:uppercase; margin-bottom: 30px; }
        .cover h1{ font-size: 48px; font-weight: 600; line-height: 1.05; margin: 0 0 20px; }
        .cover .sub{ color:#666; font-size: 18px; font-style: italic; }
        .cover .meta{ margin-top:60px; font-family:'IBM Plex Sans', sans-serif; font-size:13px; color:#444; }
        body{ font-family:'IBM Plex Sans', sans-serif; font-size:14px; line-height:1.7; }
        h1,h2,h3{ font-family:'Manrope', sans-serif; }
        h2{ color:#B8860B; border-bottom: 1px solid #ddd; padding-bottom: 6px; margin-top: 30px; }
        ul{ padding-left:20px; }
        blockquote{ border-left:3px solid #B8860B; padding-left:14px; color:#555; font-style:italic; }
        .footer{ margin-top: 60px; padding-top:20px; border-top: 1px solid #ddd; font-size:11px; color:#666; text-align:center; font-family:'JetBrains Mono', monospace; }
        @media print{ .cover{ page-break-after: always; } }
      </style></head>
      <body>
        <div class="cover">
          <div class="brand">Legal · Command Center</div>
          <h1>${selected.title}</h1>
          <div class="sub">${currentCase.title}</div>
          <div class="meta">
            <div>${currentCase.client_name || ""}</div>
            <div>${currentCase.court || ""}</div>
            <div>${currentCase.case_number || ""}</div>
            <div style="margin-top:20px; color:#B8860B; font-family:'JetBrains Mono', monospace; font-size:10px; letter-spacing:2px;">${new Date(selected.created_at).toLocaleDateString("el-GR")}</div>
          </div>
        </div>
        <div>${mdToHtml(selected.output)}</div>
        <div class="footer">CONFIDENTIAL · ATTORNEY WORK PRODUCT · ${selected.model}${selected.confidence != null ? ` · ${selected.confidence}% confidence` : ""}</div>
      </body></html>`;
    w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <div data-testid="reports-page">
      <PageHeader
        title="Αναφορές / Παραδοτέα"
        subtitle="Court-ready υπομνήματα και AI παραδοτέα. Εξαγωγή σε PDF μέσω εκτύπωσης."
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-4 space-y-4">
          <div className="surface-card p-5">
            <h3 className="font-heading font-semibold text-[13.5px] mb-3 flex items-center gap-2">
              <Sparkles className="size-4 gold-text"/>Δημιουργία νέου παραδοτέου
            </h3>
            <select value={model} onChange={e => setModel(e.target.value)} data-testid="report-model-select"
              className="w-full mb-3 bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[12.5px]">
              <option value="claude">Claude (Sonnet 5 / Opus 4.8 — νομική ανάλυση)</option>
              <option value="gpt">OpenAI GPT-5.1 (στρατηγική)</option>
            </select>
            <div className="space-y-1.5">
              {REPORT_TYPES.map(r => (
                <button
                  key={r.key}
                  data-testid={`report-gen-${r.key}`}
                  onClick={() => generate(r.key)}
                  disabled={generating === r.key}
                  className="w-full text-left p-2.5 rounded border border-border/60 hover:border-[hsl(var(--gold)/0.4)] hover:bg-muted/40 text-[12.5px] flex items-center justify-between gap-2 disabled:opacity-60 transition-all"
                >
                  <span className="truncate">{r.title}</span>
                  {generating === r.key ? <Sparkles className="size-3.5 gold-text animate-pulse-soft"/> : <Sparkles className="size-3.5 text-muted-foreground"/>}
                </button>
              ))}
            </div>
          </div>

          <div className="surface-card p-5">
            <h3 className="font-heading font-semibold text-[13.5px] mb-3">Αποθηκευμένα ({outputs.length})</h3>
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto scrollbar-thin">
              {outputs.length === 0 && <div className="text-[12px] text-muted-foreground py-2">{t("empty_state")}</div>}
              {outputs.map(o => (
                <div key={o.id} data-testid={`report-item-${o.id}`}
                  onClick={() => setSelected(o)}
                  className={`p-2.5 rounded border cursor-pointer transition-all ${selected?.id === o.id ? "border-[hsl(var(--gold)/0.6)] bg-[hsl(var(--gold)/0.08)]" : "border-border/60 hover:border-[hsl(var(--gold)/0.3)]"}`}>
                  <div className="text-[12.5px] font-medium truncate">{o.title}</div>
                  <div className="flex items-center gap-2 mt-1">
                    {o.confidence != null && <StatusBadge tone="gold">{o.confidence}%</StatusBadge>}
                    <span className="text-[10px] text-muted-foreground font-mono">{new Date(o.created_at).toLocaleDateString("el-GR")}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-8">
          {!selected ? (
            <EmptyState title="Επιλέξτε ή δημιουργήστε αναφορά" subtitle="Τα παραδοτέα εμφανίζονται με court-ready formatting." icon={FileOutput}/>
          ) : (
            <div className="surface-card overflow-hidden">
              <div className="px-6 py-4 border-b border-border/60 flex items-center justify-between gap-3 bg-muted/20">
                <div className="min-w-0">
                  <h2 className="font-heading font-semibold text-[15px] truncate">{selected.title}</h2>
                  <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{selected.model} · {new Date(selected.created_at).toLocaleString("el-GR")}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <a
                    data-testid="report-pdf-btn"
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${currentCase.id}/ai-outputs/${selected.id}/pdf`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] text-[12.5px] font-medium hover:opacity-90"
                  >
                    <Download className="size-3.5"/>PDF
                  </a>
                  <a
                    data-testid="report-docx-btn"
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${currentCase.id}/ai-outputs/${selected.id}/docx`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border/80 hover:border-[hsl(var(--gold)/0.4)] text-[12.5px]"
                  >
                    <Download className="size-3.5"/>Word
                  </a>
                  <button data-testid="report-print-btn" onClick={printReport}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border/80 hover:border-[hsl(var(--gold)/0.4)] text-[12.5px]">
                    <Printer className="size-3.5"/>Print
                  </button>
                  <button data-testid="report-delete-btn" onClick={() => remove(selected.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border/80 hover:border-red-500/40 hover:text-red-400 text-[12.5px]">
                    <Trash2 className="size-3.5"/>
                  </button>
                </div>
              </div>
              <div className="p-8 max-h-[70vh] overflow-y-auto scrollbar-thin">
                <div className="text-center mb-6 pb-6 border-b border-border/40">
                  <div className="font-mono text-[10px] gold-text uppercase tracking-[0.3em] mb-3">Legal · Command Center</div>
                  <h1 className="font-display text-3xl font-semibold mb-2">{selected.title}</h1>
                  <div className="text-[13px] italic text-muted-foreground">{currentCase.title}</div>
                  <div className="font-mono text-[10px] gold-text uppercase tracking-[0.2em] mt-4">CONFIDENTIAL · ATTORNEY WORK PRODUCT</div>
                </div>
                <div className="markdown-output prose-sm" dangerouslySetInnerHTML={{ __html: mdToHtml(selected.output) }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
